import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

function Header({ isInstructorRoute, isMobileMenuOpen, setIsMobileMenuOpen }) {
  const { currentUser, logout } = useAuth();
  const { hasActiveSubscription, getPlanDetails } = useSubscription();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // For instructor routes, only show header on mobile
  if (isInstructorRoute) {
    return (
      <header className="lg:hidden bg-klorah-card/90 backdrop-blur-md border-b border-klorah-border sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            {/* Mobile menu toggle */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 rounded-lg hover:bg-klorah-card transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            
            {/* Mobile logo without S icon */}
            <div className="flex items-center space-x-2">
              <h1 className="text-lg tracking-wider text-white font-medium">
                SESSIONS
              </h1>
              <span className="px-2 py-0.5 text-[10px] bg-klorah-primary/10 rounded text-klorah-primary font-medium">
                BY KLORAH
              </span>
            </div>
          </div>
          
          {/* User avatar */}
          <div className="flex items-center space-x-2">
            {/* Subscription Status Indicator */}
            {currentUser && (
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  hasActiveSubscription() ? 'bg-emerald-400' : 'bg-red-400'
                }`}></div>
                <span className="text-xs text-klorah-text-light">
                  {hasActiveSubscription() ? 'Pro' : 'Free'}
                </span>
              </div>
            )}
            
            <div className="w-8 h-8 bg-gradient-to-br from-klorah-primary to-klorah-secondary rounded-full flex items-center justify-center">
              <span className="text-black text-sm font-medium">
                {currentUser?.email?.[0]?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // For public routes, show full header
  return (
    <header className="bg-klorah-card/90 backdrop-blur-md border-b border-klorah-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="w-8 h-8 bg-gradient-to-br from-klorah-primary to-klorah-secondary rounded-lg flex items-center justify-center mr-3 group-hover:scale-105 transition-transform">
              <span className="text-black text-sm font-bold">S</span>
            </div>
            <h1 className="text-xl tracking-wider text-white font-medium">
              S E S S I O N S
            </h1>
            <span className="ml-2 px-2 py-0.5 text-[10px] bg-klorah-primary/10 rounded text-klorah-primary font-medium">
              BY KLORAH
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {currentUser ? (
              <>
                {/* Instructor Navigation */}
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium transition-colors tracking-wider ${
                    location.pathname === '/dashboard'
                      ? 'text-klorah-primary border-b-2 border-klorah-primary'
                      : 'text-klorah-text hover:text-white'
                  }`}
                >
                  DASHBOARD
                </Link>
                <Link
                  to="/profile"
                  className={`text-sm font-medium transition-colors tracking-wider ${
                    location.pathname === '/profile'
                      ? 'text-klorah-primary border-b-2 border-klorah-primary'
                      : 'text-klorah-text hover:text-white'
                  }`}
                >
                  PROFILE
                </Link>
                <Link
                  to="/create-workshop"
                  className={`text-sm font-medium transition-colors tracking-wider ${
                    location.pathname === '/create-workshop'
                      ? 'text-klorah-primary border-b-2 border-klorah-primary'
                      : 'text-klorah-text hover:text-white'
                  }`}
                >
                  CREATE WORKSHOP
                </Link>
                
                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="flex items-center space-x-2 text-sm font-medium text-klorah-text hover:text-white transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-klorah-primary to-klorah-secondary rounded-full flex items-center justify-center">
                      <span className="text-black text-sm font-medium">
                        {currentUser.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <svg className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 glass-panel border border-klorah-border shadow-lg rounded-xl z-10 overflow-hidden">
                      <div className="py-2">
                        <div className="px-4 py-2 text-sm text-klorah-text-light border-b border-klorah-border/50">
                          {currentUser.email}
                        </div>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          View Profile
                        </Link>
                        <Link
                          to="/edit-profile"
                          className="block px-4 py-2 text-sm text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Edit Profile
                        </Link>
                        <Link
                          to="/dashboard"
                          className="block px-4 py-2 text-sm text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/subscription-management"
                          className="block px-4 py-2 text-sm text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Subscription
                        </Link>
                        <div className="border-t border-klorah-border/50 mt-2">
                          <button
                            onClick={() => {
                              setIsMenuOpen(false);
                              handleLogout();
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Public Navigation */}
                <Link
                  to="/"
                  className={`text-sm font-medium transition-colors tracking-wider ${
                    location.pathname === '/'
                      ? 'text-klorah-primary border-b-2 border-klorah-primary'
                      : 'text-klorah-text hover:text-white'
                  }`}
                >
                  WORKSHOPS
                </Link>
                <Link
                  to="/login"
                  className="klorah-button text-sm font-medium"
                >
                  INSTRUCTOR LOGIN
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-klorah-text hover:text-white p-2 transition-colors"
            >
              <svg className={`w-6 h-6 transition-transform ${isMenuOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-klorah-border py-4">
            {currentUser ? (
              <div className="space-y-2">
                <div className="px-4 py-2 text-sm text-klorah-text-light border-b border-klorah-border/50 mb-4 bg-klorah-primary/5">
                  {currentUser.email}
                </div>
                <Link
                  to="/dashboard"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/profile"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  My Profile
                </Link>
                <Link
                  to="/edit-profile"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Edit Profile
                </Link>
                <Link
                  to="/create-workshop"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Create Workshop
                </Link>
                <Link
                  to="/subscription-management"
                  className="block px-4 py-2 text-sm text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Subscription
                </Link>
                <div className="border-t border-klorah-border/50 mt-4 pt-4">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Workshops
                </Link>
                <Link
                  to="/login"
                  className="block px-4 py-2 text-sm font-medium text-klorah-text hover:text-white hover:bg-klorah-primary/10 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Instructor Login
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header; 